import {
  call,
  put,
  select,
  take,
  fork,
  takeLatest,
  cancel
} from 'redux-saga/effects';
import {
  change,
  registerField
} from 'redux-form';
import { browserHistory } from 'react-router';
import loader from '../../../../../util/loader/loader';
import {
  LOAD_CONTENT,
  SELECT_SIK,
  SELECT_PRO,
  CONFIRM_INSTALL,
  DL_PAGE_LOADED,
  SIK_INSTALL_OPTION,
  PRO_INSTALL_OPTION,
  setData,
  updateInstallationOption,
  setTimeslots,
  setSelectedTimeSlot,
  hideFetchingSpinner,
  showFetchingSpinner,
  sikServerSelectedSelector,
  proServerSelectedSelector,
  sikAvailableSelector,
  proAvailableSelector,
  serviceStopDateSelector,
  selectedServiceReconnectDateSelector,
  selfInstallAnswerIdSelector,
  proInstallAnswerIdSelector,
  timeSlotSelector,
  selectedOptionSelector
} from './install';
import dataProvider from '../../../../../util/dataProvider/dataProvider';
import {NORMALIZE_DATA} from '../../../dataProvider/dataProvider';
import {
  navigationRoutes,
  navigationSteps
} from '../../../navigation/navigationHelpers';
import {
  showGlobalSpinner,
  hideGlobalSpinner,
  errorPageByFlow
} from '../../../common/common';

// WATCHERS
export function* loadContentWatch() {
  yield takeLatest(LOAD_CONTENT, loadContentWorker);
}

export function* normalizeWatch() {
  yield takeLatest(NORMALIZE_DATA, normalizeWorker);
}

export function* confirmInstallWatch() {
  yield takeLatest(CONFIRM_INSTALL, confirmInstallWorker);
}

// WORKERS
export function* loadContentWorker() {
  try {
    yield put(showGlobalSpinner({hideContent: true}));

    // Here we manually clear the selected installation option in case it's been cached.
    // We only persist option selection if it comes directly from the api responses.
    yield put(updateInstallationOption(''));

    const errorPage = yield select(errorPageByFlow);
    const success = yield call(dataProvider.load, [
      {state: 'auth'},
      {state: 'customer'},
      {state: 'context'},
      {state: 'moversInstall'}
    ], {errorPage});

    if (!success) {
      return;
    }

    yield put({
      type: DL_PAGE_LOADED
    });

    yield put(hideGlobalSpinner());

    let sikTask, proTask;

    const sikServerSelected = yield select(sikServerSelectedSelector);
    const proServerSelected = yield select(proServerSelectedSelector);
    const sikAvailable = yield select(sikAvailableSelector);
    const proAvailable = yield select(proAvailableSelector);

    if (proServerSelected || (proAvailable && !sikAvailable)) {
      yield fork(selectProWorker);
    }

    if (sikServerSelected || (!proAvailable && sikAvailable)) {
      yield fork(selectSikWorker);
    }

    while (true) {
      const action = yield take([SELECT_SIK, SELECT_PRO]);

      if (action.type === SELECT_SIK) {
        if (proTask) {
          yield cancel(proTask);
          yield put(hideFetchingSpinner());
        }
        sikTask = yield fork(selectSikWorker, action);
      } else if (action.type === SELECT_PRO) {
        if (sikTask) {
          yield cancel(sikTask);
        }
        proTask = yield fork(selectProWorker, action);
      }
    }

  } catch(e) {
    yield put(hideGlobalSpinner());
    browserHistory.push(navigationRoutes[navigationSteps.movers.ERROR].path);
  }
}

export function* selectSikWorker() {
  yield put(updateInstallationOption(SIK_INSTALL_OPTION));
  yield put(change('install', 'selectedOption', SIK_INSTALL_OPTION));
}

export function* selectProWorker() {
  try {
    yield put(showFetchingSpinner());
    yield put(updateInstallationOption(PRO_INSTALL_OPTION));
    yield put(change('install', 'selectedOption', PRO_INSTALL_OPTION));

    // This exposes selectedTimeSlot to validation (necessary because it's not inside a Field component).
    yield put(registerField('install', 'selectedTimeSlot', 'Field'));

    yield put(setTimeslots({
      timeSlots: []
    }));

    const errorPage = yield select(errorPageByFlow);
    const installEndpoint = navigationRoutes[navigationSteps.movers.INSTALL].endpoints.loadInstall(true);
    const proInstall = yield call(loader.request, installEndpoint, {useGlobalSpinner: false, errorPage});

    yield put(setTimeslots({
      timeSlots: proInstall.professionalInstallation.timeSlots
    }));

    const selectedTimeSlot = yield select(timeSlotSelector);

    if (!selectedTimeSlot) {
      yield put(setSelectedTimeSlot(null));
    }

    yield put(hideFetchingSpinner());
  } catch(e) {
    //console.log(e)
  }
}

export function* normalizeWorker({key, data, meta}) {
  if (key !== 'moversInstall') {
    return;
  }

  yield put(setData({
    ...meta,
    content: {
      ...data
    }
  }));
}

export function* confirmInstallWorker() {

  const errorPage = yield select(errorPageByFlow);

  try {
    const selectedOption = yield select(selectedOptionSelector);
    const selfAnswerId = yield select(selfInstallAnswerIdSelector);
    const proAnswerId = yield select(proInstallAnswerIdSelector);
    const serviceReconnectDate = yield select(selectedServiceReconnectDateSelector);
    const serviceStopDate = yield select(serviceStopDateSelector);
    const selectedTimeSlot = yield select(timeSlotSelector);

    const sikData = {
            answerId: selfAnswerId,
            reConnectServiceDate: serviceReconnectDate,
            disconnectServiceDate: serviceStopDate
          };

    const proData = {
            answerId: proAnswerId,
            proInstall: selectedTimeSlot
              ? {
                  startDateTime: selectedTimeSlot.startDateTime,
                  endDateTime: selectedTimeSlot.endDateTime,
                  id: selectedTimeSlot.id
                }
              : null,
            disconnectServiceDate: serviceStopDate
          };

    const data = selectedOption === SIK_INSTALL_OPTION ? sikData : proData;

    const sikEndpoint = navigationRoutes[navigationSteps.movers.INSTALL].endpoints.confirmSik();
    const proEndpoint = navigationRoutes[navigationSteps.movers.INSTALL].endpoints.confirmPro();
    const endpoint = selectedOption === SIK_INSTALL_OPTION ? sikEndpoint : proEndpoint;

    yield put(showGlobalSpinner({hideContent: false}));
    yield call(loader.request, endpoint, {data, useGlobalSpinner: false, errorPage});
    yield put(hideGlobalSpinner());

  } catch(e) {
    //console.log(e);
    if (e.message === 'cancel(task): argument task is undefined') {
      return;
    }
    yield put(hideGlobalSpinner());

    const receivedErrorResponse = e.data && e.data.responseStatus === "Error";

    if (!receivedErrorResponse) {
      browserHistory.push(errorPage);
    }
  }
}

export default function* moversInstallSagas() {
  yield [
    fork(loadContentWatch),
    fork(normalizeWatch),
    fork(confirmInstallWatch)
  ];
}



/** WEBPACK FOOTER **
 ** ./client/redux/modules/pages/Movers/install/installSagas.js
 **/